import { useCallback } from "react"
import { useSelector } from "react-redux"
import { isAddonProduct } from "modules/products"
import { useGetData, usePostData } from "services/sales"
import { handleResponse } from "utils/fetch"

export const PRODUCT_GROUP_TYPE_SCOOTER = 0

export const PRODUCT_TYPE_SCOOTER = 1
export const PRODUCT_TYPE_PRODUCT = 4
export const PRODUCT_TYPE_AGENCY_FEE = 6
export const PRODUCT_TYPE_GIFT = 8
export const PRODUCT_TYPE_EVENT_GIFT = 9
export const PRODUCT_TYPE_BADGE = 10

export const payloadTemplate = {
  Bag: null,
  UserId: null,
  OrderNo: null,
  OrderType: 1, // 1:一般訂單 2:法人贈車
  SalesChannelNo: null,
  SalesChannelName: null,
  SalesStoreNo: null,
  SalesStoreName: null,
  DiscountPlanId: null,
  EmployeeNo: null,
  CheckEmployeeNo: null,
  Recommended: null,
  Remark: null,
  Invoice: {
    Numbers: null,
    GUINumbers: null,
    Donation: null,
    DonationNumbers: null,
  },
  Discount: [],
  ProjectActivity: [],
  Buyer: {
    Email: null,
    Name: null,
    IDCard: null,
    Mobile: null,
    City: null,
    CityId: null,
    District: null,
    DistrictId: null,
    ZipCode: null,
    Address: null,
  },
  Owner: {
    Email: null,
    Name: null,
    IDCard: null,
    Gender: null,
    Birthday: null,
    Mobile: null,
    City: null,
    CityId: null,
    District: null,
    DistrictId: null,
    ZipCode: null,
    Address: null,
    ResidenceCity: null,
    ResidenceCityId: null,
    ResidenceDistrict: null,
    ResidenceDistrictId: null,
    ResidenceZipCode: null,
    ResidenceAddress: null,
    CivilServants: null,
    PurchaseMethod: null,
  },
  Driver: {
    Name: null,
    Email: null,
    Mobile: null,
    Birthday: null,
    City: null,
    CityId: null,
    District: null,
    DistrictId: null,
    ZipCode: null,
    Address: null,
  },
  Deliveries: {
    Name: null,
    Email: null,
    Mobile: null,
    City: null,
    CityId: null,
    District: null,
    DistrictId: null,
    ZipCode: null,
    Address: null,
  },
  DeliveryConditions: {
    Type: null,
    PickupTime: null,
    StoreId: null,
  },
  Scooter: {
    PDIStoreId: null,
    PairingAccount: null,
    SubsidyType: null,
    SubsidyApplication: null,
    SubsidyCounty: null,
    SingingDay: null,
    BeDeprecatedSamePerson: null,
    Licensing: {
      LicensingStatus: null,
      LicensingLocation: null,
      InsuredYears: null,
      Stamp: null,
      ChooseLicensing: null,
      ChooseRemark: null,
    },
  },
  Payment: {
    Type: null,
    CreditCard: {},
    Cash: {},
    Loan: {},
    Receivables: {},
  },
  Contract: {
    InvoicesGUINumbers: null,
    InvoicesTitle: null,
  },
  Recommend: {
    Email: null,
    Name: null,
    Mobile: null,
    City: null,
    CityId: null,
    District: null,
    DistrictId: null,
    ZipCode: null,
    Address: null,
    LicensePlate: null,
  },
}

export const calcShopCartAmount = (cart) => {
  if (cart && ((cart.length === 1 && cart[0].Product.length > 0) || cart.length > 1)) {
    let currentTotal = 0
    let currentDiscount = 0

    cart.forEach((proList) => {
      proList.Product.forEach((pro) => {
        currentTotal += pro.Price * pro.Quantity
        currentDiscount += pro.DiscountAmount
      })
    })

    return { total: currentTotal, discount: currentDiscount }
  } else {
    return { total: 0, discount: 0 }
  }
}

export const isProductCartIncludesAddon = (productCart) => {
  return !!productCart?.find((item) => item?.Product?.find((product) => isAddonProduct(product)))
}

export const isItemsInCart = (cart) => {
  return !!cart?.find((item) => item?.Product?.length)
}

export const useCart = () => {
  const removeCartItem = useGetData(useCallback((removeItemId) => `/tw/api/shopping-cart/remove/${removeItemId}`, []))
  const recalculateProduct = usePostData("/tw/api/shopping-cart/recalculate-order-product")
  const postOrder = usePostData("/tw/api/shopping-cart/checkout")
  const batchOrder = usePostData("/tw/api/shopping-cart/post-wholesale-batch-order")
  const reportSubscription = usePostData("/tw/api/shopping-cart/post-wholesale-order-escontract-id")
  const updateChange = usePostData("/tw/api/shopping-cart/change-cart")
  const postPromotion = usePostData("/tw/api/utility/set-retail-order-promotion")
  const deletePromotion = usePostData(
    useCallback((promotionId) => `/tw/api/utility/remove-retail-order-promotion?PromotionId=${promotionId}`, []),
  )
  const postBuyerIdentity = usePostData("/tw/api/utility/set-buyer-identity")
  const postRemoveIdentity = usePostData("/tw/api/utility/remove-buyer-identity")
  // const postScooterAddon = usePostData('/tw/api/utility/digital-products-verification');
  const postScooterAddon = usePostData("/tw/api/utility/products-verification")
  const postProductsVerification = usePostData("/tw/api/utility/products-verification")

  const recalculateCartProduct = useCallback(
    (data) =>
      recalculateProduct(data)
        .then(handleResponse)
        .then((response) => response.Data),
    [recalculateProduct],
  )

  const updateCart = useCallback((change) => updateChange(change).then(handleResponse), [updateChange])

  const placeOrder = useCallback(
    (payload) =>
      postOrder(payload)
        .then(handleResponse)
        .then((response) => response.Data),
    [postOrder],
  )

  const orderWholeSale = useCallback(
    (payload) => {
      return batchOrder(payload)
        .then(handleResponse)
        .then((response) => response.Data)
    },
    [batchOrder],
  )

  const subscribeWholeSale = useCallback(
    (payload) => {
      return reportSubscription(payload).then(handleResponse)
    },
    [reportSubscription],
  )

  const applyPromotion = useCallback(
    (promotions) => {
      return postPromotion({ Promotions: promotions }).then(handleResponse)
    },
    [postPromotion],
  )

  const removePromotion = useCallback(
    (promotionId) => {
      return deletePromotion(promotionId).then(handleResponse)
    },
    [deletePromotion],
  )

  const attachEmployeeIdentity = useCallback(
    ({ email, number }) =>
      postBuyerIdentity({
        Type: 1,
        Email: email,
        IdentityNo: number,
      }).then(handleResponse),
    [postBuyerIdentity],
  )

  const removeEmployeeIdentity = useCallback(() => postRemoveIdentity().then(handleResponse), [postRemoveIdentity])

  const verifyScooterAddon = useCallback(
    ({ email, code, plate, addonCodes }) => {
      return postScooterAddon({
        Email: email,
        Phone: code,
        PlateNo: plate,
        AddonCodes: addonCodes,
      })
        .then(handleResponse)
        .then((response) => response.Data)
    },
    [postScooterAddon],
  )

  const verifyProductsVerification = useCallback(
    ({ email, code }) => {
      return postProductsVerification({
        Email: email,
        Phone: code,
      })
        .then(handleResponse)
        .then((response) => response.Data)
    },
    [postProductsVerification],
  )

  return {
    removeCartItem,
    recalculateCartProduct,
    updateCart,
    placeOrder,
    orderWholeSale,
    subscribeWholeSale,
    applyPromotion,
    removePromotion,
    attachEmployeeIdentity,
    removeEmployeeIdentity,
    verifyScooterAddon,
    verifyProductsVerification,
  }
}

export const useProductCart = () => {
  // TODO create a CartContext to replace redux
  return useSelector((state) => state.product.shopCart)
}
